import React, { useState, useEffect, useRef } from "react";
import { Skeleton } from "@mui/material";
import ReactPaginate from "react-paginate";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import AddCustomer from "./AddCustomer";
import EditCutomer from "./EditCustomer";
import DeleteModal from "../../../constants/DeleteModal";
import { Link } from "react-router-dom";
import { usePermission } from "../../../context/PermissionContext";
import CustomerView from "./CustomerView";
import Button from "../recharge/Button";
import AddGroup from "./AddGroup";
import EditGroup from "./EditGroup";
import { useAuth } from "../../../context/AuthProvider";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import ProductSearchList from "../../../constants/ProductSearchList";
import {
  AddIcon,
  CircleIcon,
  DownloadIcon,
  SearchIcon,
  SortDownIcon,
  SpreadSheetIcon,
} from "../../../constants/Icons";

const AllCustomer = ({ isActive }) => {
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomerData, setCustomerData] = useState([]);
  const [isGroupLoading, setIsGroupLoading] = useState(false);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
  const searchInputRef = useRef(null);
  const customerSuggestionsRef = useRef(null);
  const suggestionRefs = useRef([]);
  const [addGroupload, setAddGroupLoad] = useState(false);
  const { updateSettings, isUser } = useAuth();
  const [isSearchTerm, setSearchTerm] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomerPhone, setSelectedCustomerPhone] = useState("");
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1); // -1 = none selected

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 10;

  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isEditData, setEditData] = useState(null);
  const [isDeleteData, setDeleteData] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [groupName, setGroupName] = useState([]);
  const [addNewGroupOpen, setNewGroupOpen] = useState(false);
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const {
    permission,
    hasPermission,
    customerGroups,
    blockedPermissions,
    viewOption,
  } = usePermission();
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const [selectAll, setSelectAll] = useState(false);
  const [newGroupName, setNewGroupName] = useState(null);
  const [error, setErrors] = useState({});
  const [selectedFilter, setSelectedFilter] = useState({
    field: "createdAt",
    order: "asc",
  });
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const handleNewGroupClose = () => {
    setNewGroupOpen(false);
  };
  const handleEditGroupClose = () => {
    setEditGroupOpen(false);
  };
  const handleEditGroupOpen = () => {
    setEditGroupOpen(true);
  };
  const handleNewGroupOpen = () => {
    setNewGroupOpen(true);
  };
  const handleListCustomer = async (
    page = 0,
    term = "",
    sortbyField = "createdAt",
    sortOrder = "asc",
  ) => {
    if (isLoading) return;
    try {
      if (!checkNetwork()) return;
      setIsLoading(true);
      // const data = { searchtxt: term, pageno: page + 1, limit: perPage };
      const response = await AxiosInstance.get(
        `module/customer/all?pageno=${page + 1}&limit=${perPage}&searchtxt=${term}&sortby=${sortOrder}&sortbyField=${sortbyField}`,
      );
      const results = response.data.results || [];
      setCustomerData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (term) => {
    if (
      hasPermission("CUSTOMER", "CUSTOMER:search") ||
      permission === undefined
    ) {
      setSearchTerm(term);
      setHasSearched(true);
      setSelectedCustomerPhone("");

      if (!term?.trim()) {
        setCustomerSuggestions([]);
        if (selectedGroupId) {
          handleGroupList(0, " ");
        } else {
          handleListCustomer(0, "");
        }
        setCurrentPage(0);
        return;
      }

      //  Clear previous debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      //  Cancel previous API request if still running
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      debounceTimerRef.current = setTimeout(async () => {
        try {
          if (!checkNetwork()) return;
          setSuggestionLoading(true);

          // Create new AbortController for this request
          controllerRef.current = new AbortController();

          const response = await AxiosInstance.get(
            `module/customer?searchtxt=${term?.toLowerCase()}`,
            { signal: controllerRef.current.signal },
          );

          setCustomerSuggestions(response.data.results || []);
        } catch (error) {
          if (error.name !== "CanceledError" && error.name !== "AbortError") {
            handleError(error, showAlert);
          }
        } finally {
          setSuggestionLoading(false);
        }
      }, 200);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleSearchFind = () => {
    const currentInputValue = searchInputRef.current?.value || "";
    setSearchTerm(currentInputValue);
    setSelectedCustomerPhone("");
    if (!currentInputValue?.trim()) {
      handleListCustomer(0, "");
    } else {
      handleListCustomer(0, currentInputValue);
    }
    setCustomerSuggestions([]);
    setHasSearched(false);
    setCurrentPage(0);
  };

  const handleSelectCustomer = (customer) => {
    const phone = customer.phone;
    setSearchTerm(phone);
    setSelectedCustomerPhone(phone);
    setCustomerSuggestions([]);
    setHasSearched(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    handleListCustomer(0, phone);
    setCurrentPage(0);
  };

  const handleOutsideClick = (e) => {
    if (
      customerSuggestionsRef.current &&
      !customerSuggestionsRef.current.contains(e.target)
    ) {
      setCustomerSuggestions([]);
      setHasSearched(false);
    }
  };

  const handleOpenModal = () => {
    if (hasPermission("CUSTOMER", "CUSTOMER:add") || permission === undefined) {
      setModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleView = (customer) => {
    setSelectedCustomerId(customer._id); // pass the customer id
    setViewModalOpen(true);
  };

  const handleAdd = async (newData) => {
    try {
      if (!checkNetwork()) return;
      const data = {
        ...newData,
        shippingAddress: {
          addressline1: newData.shippingaddressline1,
          addressline2: newData.shippingaddressline2,
          district: newData.shippingdistrict,
          city: newData.shippingcity,
          state: newData.shippingstate,
          statecode: newData.shippingstatecode,
          country: newData.shippingcountry,
          pincode: newData.shippingpincode,
        },

        // ✅ Contact Person Object
        contactPerson: {
          name: newData.contactPersonname,
          number: newData.contactPersonNumber,
        },
      };
      if (!data.groupid) {
        delete data.groupid;
      }
      const response = await AxiosInstance.post(`module/customer`, data);
      showAlert("success", Messages.SUCCESS.CUSTOMER_ADDED);
      // await handleListCustomer(0);
      const result = response.data.results;
      if (result.name !== newData.name) {
        showAlert(
          "warning",
          `The phone number"${result.phone}" already exists .`,
        );
        handleCloseModal();
        return true;
      }
      let isLocalDuplicate = false;

      setCustomerData((prev) => {
        if (prev.some((it) => it.name === result.name)) {
          isLocalDuplicate = true;
          return prev;
        } else {
          let newArray = [result, ...prev];

          if (newArray.length > 10) {
            return newArray.slice(0, -1);
          } else {
            return newArray;
          }
        }
      });
      if (isLocalDuplicate) {
        showAlert(
          "warning",
          `The phone number "${result.phone}" already exists in the list.`,
        );
      } else {
        showAlert("success", Messages.SUCCESS.CUSTOMER_ADDED);
        setTotalItems((prevCount) => prevCount + 1);
      }
      // setCurrentPage(0);
      handleCloseModal();
      return true;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 409 &&
        error.response.data &&
        error.response.data.code === "DUPLICATE_ENTRY"
      ) {
        const errorMessage = error.response.data.message;
        let fieldError = {};

        // if (errorMessage.includes("ReferenceId already exists")) {
        //     fieldError.referenceid = "Reference ID already exists .";
        // }
        if (errorMessage.includes("GST number already exists")) {
          fieldError.gstnumber = "GST number already exists.";
        }

        if (Object.keys(fieldError).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldError }));
        } else {
          handleError(error, showAlert);
        }

        return false;
      } else {
        handleError(error, showAlert);
        return false;
      }
    }
  };

  const openEditModal = (cItem) => {
    if (blockedPermissions("CUSTOMER:view") || permission === undefined) {
      setEditData(cItem);
      setEditModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const closeEditModal = () => {
    setEditData(null);
    setEditModalOpen(false);
  };

  const hanleUpdateCustomer = (data) => {
    setCustomerData((prevCustomerData) =>
      prevCustomerData?.map((customer) =>
        customer?._id === data?._id ? data : customer,
      ),
    );
  };
  const handleUpdte = async (updatedData) => {
    const editableKeys = [
      "name",
      "phone",
      "email",
      "addressline1",
      "addressline2",
      "city",
      "district",
      "state",
      "pincode",
      "country",
      "gstnumber",
      "pannumber",
      "cinnumber",
      "referenceid",
      "isPayLater",
      "groupid",
      "fssai",
      "tradeName",
      "nickname",
      "contactPersonname",
      "contactPersonNumber",
      "shippingaddressline1",
      "shippingaddressline2",
      "shippingstate",
      "shippingcity",
      "shippingdistrict",
      "shippingpincode",
      "shippingcountry",
      "website",
    ];

    let hasChange = false;
    for (const key of editableKeys) {
      const originalValue = String(isEditData[key] || "");
      const updatedValue = String(updatedData[key] || "");
      if (originalValue !== updatedValue) {
        hasChange = true;
        break;
      }
    }

    if (!hasChange) {
      showAlert("warning", "No changes detected.");
      return false;
    }
    try {
      if (!checkNetwork()) return;
      const data = {
        ...updatedData,
        shippingAddress: {
          addressline1: updatedData.shippingaddressline1,
          addressline2: updatedData.shippingaddressline2,
          district: updatedData.shippingdistrict,
          city: updatedData.shippingcity,
          state: updatedData.shippingstate,
          statecode: updatedData.shippingstatecode,
          country: updatedData.shippingcountry,
          pincode: updatedData.shippingpincode,
        },

        // ✅ Contact Person Object
        ...(updatedData.contactPersonname?.trim() ||
        updatedData.contactPersonNumber?.trim()
          ? {
              contactPerson: {
                ...(updatedData.contactPersonname?.trim() && {
                  name: updatedData.contactPersonname,
                }),
                ...(updatedData.contactPersonNumber?.trim() && {
                  number: updatedData.contactPersonNumber,
                }),
              },
            }
          : {}),
        customerid: updatedData._id,
      };
      const response = await AxiosInstance.patch(
        `module/customer/${updatedData._id}`,
        data,
      );
      showAlert("success", Messages.SUCCESS.CUSTOMER_UPDATED);
      setCustomerData((prevCustomerData) =>
        prevCustomerData.map((customer) =>
          customer._id === response.data.results?._id
            ? response.data.results
            : customer,
        ),
      );
      closeEditModal();
      return true;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 409 &&
        error.response.data &&
        error.response.data.code === "DUPLICATE_ENTRY"
      ) {
        const errorMessage = error.response.data.message;
        let fieldError = {};

        // if (errorMessage.includes("ReferenceId already exists")) {
        //     fieldError.referenceid = "Reference ID already exists .";
        // }
        if (errorMessage.includes("GST number already exists")) {
          fieldError.gstnumber = "GST number already exists.";
        }

        if (Object.keys(fieldError).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldError }));
        } else {
          handleError(error, showAlert);
        }

        return false;
      } else {
        handleError(error, showAlert);
        return false;
      }
    }
  };

  const handleCustomerGroupAdd = async (groupName) => {
    setAddGroupLoad(true);
    try {
      const data = {
        name: groupName,
      };
      const response = await AxiosInstance.post("module/customer/group", data);
      const results = response.data.results;
      //  setGroupName(results);
      setGroupName((prev) => [
        { ...results },
        ...(Array.isArray(prev) ? prev : []),
      ]);
      updateSettings({
        customerGroups: [
          { ...results },
          ...(Array.isArray(customerGroups) ? customerGroups : []),
        ],
      });
      showAlert("success", "Customer Group Added Successfull");
      setAddGroupLoad(false);
      handleNewGroupClose();
      setNewGroupName(results);
      handleOpenModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setAddGroupLoad(false);
    }
  };
  const handleCustomerGroupEdit = async (groupName, id) => {
    setAddGroupLoad(true);
    try {
      const data = {
        name: groupName,
      };
      const response = await AxiosInstance.patch(
        `module/customer/group/${id}`,
        data,
      );
      const results = response.data.results;
      //  setGroupName(results);
      setGroupName((prev) => {
        if (!Array.isArray(prev)) return [{ ...results }]; // safeguard

        return prev.map((g) =>
          g._id === results._id ? { ...g, ...results } : g,
        );
      });
      setSelectedGroupDetails(results);
      setAddGroupLoad(false);
      showAlert("success", "Customer Group Updated");
      handleEditGroupClose();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setAddGroupLoad(false);
    }
  };

  const handleGroupList = async (page = 0, term = "") => {
    setIsGroupLoading(true);
    try {
      const response = await AxiosInstance.get(
        `module/customer/group/list?pageno=${page + 1}&limit=${perPage}`,
      );
      const results = response.data.results;
      setGroupName(results);
      setIsGroupLoading(false);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsGroupLoading(false);
    }
  };

  const handleSelectAll = async () => {
    setSelectAll(true);
    setCustomerData("");
    setSelectedGroupId(null);
    setSelectedGroupDetails("");
    setCurrentPage(0);
    await handleListCustomer(0, "");
  };

  const handleGroupCustomerList = async (
    item,
    page = 0,
    sortbyField = "createdAt",
    sortOrder = "asc",
  ) => {
    setCurrentPage(0);
    setBtnLoading(false);
    try {
      const response = await AxiosInstance.get(
        `module/customer/grouped/${item._id}?pageno=${
          page + 1
        }&limit=${perPage}&sortby=${sortOrder}&sortbyField=${sortbyField}`,
      );
      const results = response.data.results;
      setCustomerData(results);
      setSelectedGroupId(item._id);
      setSelectedGroupDetails(item);
      setSelectAll(false);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
      setBtnLoading(false);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };
  const openDeleteModal = (cItem) => {
    if (
      hasPermission("CUSTOMER", "CUSTOMER:delete") ||
      permission === undefined
    ) {
      setDeleteData(cItem);
      setDeleteModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleDeleteCustomer = (data) => {
    setCustomerData((prevData) =>
      prevData?.filter((customer) => customer._id !== data?._id),
    );
    setTotalItems((prev) => prev - 1);
  };

  const handleDelete = async () => {
    try {
      if (!checkNetwork()) return;
      setBtnLoading(true);
      // const data = { customerid: isDeleteData._id };
      await AxiosInstance.delete(`module/customer/${isDeleteData._id}`);
      showAlert("success", Messages.SUCCESS.CUSTOMER_DELETED);
      setCustomerData((prevData) =>
        prevData.filter((customer) => customer._id !== isDeleteData._id),
      );

      setTotalItems((prev) => prev - 1);

      // const newTotal = totalItems - 1;
      // const newTotalPages = Math.ceil(newTotal / perPage);

      // if (isCustomerData.length === 1 && currentPage > 0) {
      //   setCurrentPage((prev) => prev - 1);
      //   await handleListCustomer(currentPage - 1, isSearchTerm);
      // } else {
      //   await handleListCustomer(currentPage, isSearchTerm);
      // }

      // setTotalItems(newTotal);
      // setTotalPages(newTotalPages);
      setDeleteModalOpen(false);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const exportToCSV = async () => {
    try {
      if (!totalItems) {
        showAlert("warning", "No data available to export");
        return;
      }

      if (!checkNetwork()) return;

      setBtnLoading(true);

      let response;

      const endpoint =
        isUser.stype === "MAIN" ? "module/customer/all" : "module/customer";

      response = await AxiosInstance.get(
        `${endpoint}?pageno=1&limit=${totalItems}&searchtxt=${encodeURIComponent(
          isSearchTerm || "",
        )}&sortby=${selectedFilter.order}&sortbyField=${selectedFilter.field}`,
      );

      const customers = response.data.results || [];

      if (!customers.length) {
        showAlert("warning", "No data available to export");
        return;
      }

      const headers =
        "Name,NickName,Phone,Email,Role,WholeSale,Group,Address Line 1,Address Line 2,City,District,State,Pincode,Country,GST Number,Pan Number,Cin Number,FSSAI,Business Name,Trade Name,Website,Contact Person Name,Contact Person Number,Reference No,Note,Closing Balance,Balance Side, Paylater Amount,Shipping Address Line 1,Shipping Address Line 2,Shipping City,Shipping District,Shipping State,Shipping Pincode,Shipping Country";

      const clean = (val) => (val ?? "").toString().replace(/[\r\n;]/g, " ");

      const quote = (val) => `"${clean(val).replace(/"/g, '""')}"`;

      const rows = customers
        .map((item) => {
          const row = [
            quote(item?.name),
            quote(item?.nickname),
            quote(item.phone),
            quote(item?.email),
            quote(item?.role),
            quote(item?.wholesale),
            quote(item?.groupName),
            quote(item?.addressline1),
            quote(item?.addressline2),
            quote(item?.city),
            quote(item?.district),
            quote(item?.state),
            quote(item?.pincode),
            quote(item?.country),
            quote(item?.gstnumber),
            quote(item?.pannumber),
            quote(item?.cinnumber),
            quote(item?.fssai),
            quote(item?.businessname),
            quote(item?.tradeName),
            quote(item?.website),
            quote(item?.contactPerson?.name),
            quote(item?.contactPerson?.number),
            quote(item?.referenceId || item?.referenceid),
            quote(item?.note),
            quote(
              CurrencyConverter.toCurrency(item?.paylaterAmount || 0, "INR"),
            ),
            quote(item?.balanceside),
            quote(
              CurrencyConverter.toCurrency(item?.paylaterAmount || 0, "INR"),
            ),
            quote(item?.shippingAddress?.addressline1),
            quote(item?.shippingAddress?.addressline2),
            quote(item?.shippingAddress?.city),
            quote(item?.shippingAddress?.district),
            quote(item?.shippingAddress?.state),
            quote(item?.shippingAddress?.pincode),
            quote(item?.shippingAddress?.country),
          ];

          return row.join(",");
        })
        .join("\n");

      const csvContent = `${headers}\n${rows}`;

      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `CustomerList_${formatDate(new Date())}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setBtnLoading(false);
    }
  };

  const getColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-label-success";
      case "inactive":
        return "bg-label-danger";
      default:
        return "bg-label-secondary";
    }
  };

  useEffect(() => {
    const handleArrowAndEnter = (e) => {
      // Only handle when search term is empty or suggestions list is empty
      if (!(isSearchTerm.trim() === "" || customerSuggestions.length === 0))
        return;

      if (
        isModalOpen ||
        isViewModalOpen ||
        isEditData ||
        isDeleteModalOpen ||
        addNewGroupOpen ||
        editGroupOpen
      )
        return;

      if (!["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) return;

      e.preventDefault();

      const tableRows = Array.from(document.querySelectorAll("table tbody tr"));
      if (!tableRows.length) return;

      const currentRow = document.activeElement.closest("tr");
      let currentIndex = tableRows.indexOf(currentRow);

      // Handle Enter key
      if (e.key === "Enter") {
        if (currentIndex >= 0) {
          const item = tableRows[currentIndex].dataset.item
            ? JSON.parse(tableRows[currentIndex].dataset.item)
            : null;

          if (item) handleView(item);
        }
        return;
      }

      // Virtual -1: no row focused yet
      if (currentIndex === -1) {
        if (e.key === "ArrowDown") {
          tableRows[0].tabIndex = 0;
          tableRows[0].focus();
        } else if (e.key === "ArrowUp") {
          tableRows[tableRows.length - 1].tabIndex = 0;
          tableRows[tableRows.length - 1].focus();
        }
        return;
      }

      // Remove tabIndex from current row
      currentRow.tabIndex = -1;

      // Calculate next/previous index
      let nextIndex =
        e.key === "ArrowDown"
          ? (currentIndex + 1) % tableRows.length
          : (currentIndex - 1 + tableRows.length) % tableRows.length;

      const nextRow = tableRows[nextIndex];
      nextRow.tabIndex = 0;
      nextRow.focus();
    };

    window.addEventListener("keydown", handleArrowAndEnter);
    return () => window.removeEventListener("keydown", handleArrowAndEnter);
  }, [
    isSearchTerm,
    customerSuggestions,
    isModalOpen,
    isViewModalOpen,
    isEditData,
    isDeleteModalOpen,
    addNewGroupOpen,
    editGroupOpen,
  ]);

  const handleSuggestionNavigation = (e) => {
    if (e.key === "Enter") {
      if (activeIndex >= 0 && customerSuggestions[activeIndex]) {
        e.preventDefault();
        handleSelectCustomer(customerSuggestions[activeIndex]);
      } else {
        e.preventDefault();
        handleSearchFind();
      }
      if (isSearchTerm.trim() === "" || customerSuggestions.length === 0) {
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();

      if (isSearchTerm.trim() === "" || customerSuggestions.length === 0) {
      }

      setActiveIndex((prev) =>
        prev < customerSuggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : customerSuggestions.length - 1,
      );
    }
  };

  useEffect(() => {
    if (activeIndex >= 0 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    searchInputRef.current?.focus();
    handleGroupList();
    setSelectAll(true);
  }, []);

  useEffect(() => {
    if (
      hasPermission("CUSTOMER", "CUSTOMER:list") ||
      permission === undefined
    ) {
      handleListCustomer(currentPage, isSearchTerm);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  }, [currentPage]);

  const handleFilterChange = (e) => {
    const [field, order] = e.target.value.split("-");
    setSelectedFilter({ field, order });
  };

  const handleGroupChange = (group) => {
    setCurrentPage(0);
    setSelectedGroupDetails(group);
    if (group && group._id) {
      handleGroupCustomerList(group);
    } else {
      setSelectAll(true);
      setSelectedGroupId(null);
      setSelectedGroupDetails("");
      handleListCustomer();
    }
  };
  const isReferenceIdVisible = viewOption("customerSettings", "isReferenceId");
  const totalSkeletonColumns = 5 + (isReferenceIdVisible ? 1 : 0);

  useEffect(() => {
    handleListCustomer(
      currentPage,
      isSearchTerm,
      selectedFilter.field,
      selectedFilter.order,
    );
  }, [selectedFilter]);

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="row">
          <div className="col-12">
            <div className="page-breadcrumb d-flex align-items-center paddingBottom">
              <div className="d-flex align-items-center  pe-3">
                <div className=" d-flex align-items-center">
                  <span className="breadcrumb-title me-1">All</span>
                  <span className="breadcrumb-title">Customers</span>
                </div>
                <div className="ms-3 col-lg-12">
                  <div className="position-relative">
                    <div className="input-group" ref={customerSuggestionsRef}>
                      <input
                        type="text"
                        className="form-control"
                        ref={searchInputRef}
                        onChange={(e) => {
                          handleSearch(e.target.value);
                          setActiveIndex(-1);
                        }}
                        onKeyDown={handleSuggestionNavigation}
                        autoComplete="off"
                        placeholder="Search customer by name, phone, email..."
                      />
                      <button
                        className="input-group-text"
                        onClick={handleSearchFind}
                      >
                        <SearchIcon />
                      </button>
                    </div>

                    {hasSearched &&
                      isSearchTerm &&
                      !suggestionLoading &&
                      customerSuggestions.length === 0 && (
                        <ul className="suggestions-list">
                          <li className="suggestion-item text-muted">
                            No customers data was found
                          </li>
                        </ul>
                      )}

                    {customerSuggestions.length > 0 && (
                      <ProductSearchList
                        suggest={customerSuggestions}
                        suggestionRefs={suggestionRefs}
                        handleSelect={handleSelectCustomer}
                        activeIndex={activeIndex}
                        type="customer"
                      />
                    )}
                  </div>
                </div>
                {/* {selectedGroupDetails?._id && (
                  <>
                    -{" "}
                    <span
                      className="ms-1 d-flex align-items-center justify-content-between badge rounded-10 bg-label-info cursor-poInter p-2"
                      onClick={() => handleEditGroupOpen()}
                    >
                       <span className="me-2">
                       <i className="ng ng-ng-customers-icon fs-6"></i>
                      </span>
                      <span className="text-primary">{selectedGroupDetails?.name}</span>
                      <span className="ms-2">
                        <i className="ng ng-ng-edit-icon"></i>
                      </span>
                    </span>
                  </>
                )} */}
              </div>

              <div className="ms-auto d-flex gap-2">
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-export dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <DownloadIcon /> Export
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <Link className="dropdown-item" onClick={exportToCSV}>
                        <SpreadSheetIcon />{" "}
                        <span className="icon-text">Excel (CSV)</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col">
                <div className="card radius-10 mb-0">
                  <div className="card-body px-0">
                    <div className="row px-3">
                      {/* <div className="col-lg-4 col-12 ">
                        <div className="position-relative">
                          <div
                            className="input-group"
                            ref={customerSuggestionsRef}
                          >
                            <input
                              type="text"
                              className="form-control"
                              ref={searchInputRef}
                              onChange={(e) => {
                                handleSearch(e.target.value);
                                setActiveIndex(-1);
                              }}
                              onKeyDown={handleSuggestionNavigation}
                              autoComplete="off"
                              placeholder="Search customer by name, phone, email..."
                            />
                            <button
                              className="input-group-text"
                              onClick={handleSearchFind}
                            >
                              <i className="bx bx-search"></i>
                            </button>
                          </div>

                          {hasSearched &&
                            isSearchTerm &&
                            !suggestionLoading &&
                            customerSuggestions.length === 0 && (
                              <ul className="suggestions-list">
                                <li className="suggestion-item text-muted">
                                  No customers data was found
                                </li>
                              </ul>
                            )}

                          {customerSuggestions.length > 0 && (
                            <ul className="suggestions-list">
                              {customerSuggestions.map((customer, index) => (
                                <li
                                  key={customer._id}
                                  ref={(el) =>
                                    (suggestionRefs.current[index] = el)
                                  }
                                  onClick={() => handleSelectCustomer(customer)}
                                  className={`suggestion-item ${
                                    index === activeIndex
                                      ? "active-suggestion"
                                      : ""
                                  }`}
                                >
                                  {customer.name} -
                                  <span className="badge bg-label-blue rounded-pill ms-1">
                                    {customer.phone}
                                  </span>
                                  {customer.referenceid && (
                                    <>
                                      <small>-</small>
                                      <span className="badge bg-label-secondary rounded-pill ms-1">
                                        {customer.referenceid}
                                      </span>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div> */}
                      <div className="col-lg-3  col-6 d-flex align-items-center">
                        <div>
                          <strong>Total Customers:</strong>{" "}
                          <span className="badge btn-label-primary fs-15 ">
                            {totalItems || 0}
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-3 col-12 mb-2 mb-lg-0">
                        <div className="input-group">
                          <span className="input-group-text">
                            <SortDownIcon />
                          </span>
                          <select
                            className="form-select"
                            onChange={handleFilterChange}
                            value={`${selectedFilter.field}-${selectedFilter.order}`}
                          >
                            <option value="createdAt-asc">
                              Created At (asc)
                            </option>
                            <option value="createdAt-desc">
                              Created At (desc)
                            </option>
                            {viewOption(
                              "customerSettings",
                              "isReferenceId",
                            ) && (
                              <>
                                <option value="referenceid-asc">
                                  Reference No (asc)
                                </option>
                                <option value="referenceid-desc">
                                  Reference No (desc)
                                </option>
                              </>
                            )}
                          </select>
                        </div>
                      </div>
                      <div className="col-lg-6 col-6 d-flex justify-content-end ">
                        <button
                          type="button"
                          onClick={handleOpenModal}
                          className="btn btn-primary mt-2 mt-lg-0"
                        >
                          <AddIcon /> Add Customer
                        </button>
                      </div>
                    </div>
                    <>
                      <div className="table-responsive mt-3">
                        <table className="table align-middle mb-0">
                          <thead className="table-light text-center">
                            <tr>
                              <th>S.No</th>
                              {viewOption(
                                "customerSettings",
                                "isReferenceId",
                              ) && <th>Reference Number</th>}
                              <th>Name</th>
                              <th>Phone</th>
                              <th>Email</th>
                              {/* <th>State</th>
                              <th>Pincode</th> */}
                              <th>GST Number</th>
                              {/* <th>Actions</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              [...Array(9)].map((_, index) => (
                                <tr key={index}>
                                  {[...Array(totalSkeletonColumns)].map(
                                    (__, i) => (
                                      <td key={i} className="text-center">
                                        <Skeleton
                                          variant="text"
                                          width={i === 0 ? 300 : 200}
                                        />
                                      </td>
                                    ),
                                  )}
                                </tr>
                              ))
                            ) : isCustomerData.length > 0 ? (
                              isCustomerData.map((item, index) => (
                                <tr
                                  key={item._id || index}
                                  className="position-relative  cursor-poInter hover-row"
                                  onClick={() => handleView(item)}
                                  role="button"
                                  data-item={JSON.stringify(item)}
                                >
                                  <td className="text-center paddingTopBottom">
                                    {/* {currentPage * perPage + index + 1} */}
                                    {totalItems -
                                      (currentPage * perPage + index)}
                                    {/* {isCustomerData.length - index} */}
                                    {item.status === "ACTIVE" ? (
                                      <span className="rounded fsz-10 ms-1">
                                        <CircleIcon iClass={"text-success"} />
                                      </span>
                                    ) : (
                                      <span className="rounded fsz-10 ms-1">
                                        <CircleIcon
                                          iClass={"text-light-gray"}
                                        />
                                      </span>
                                    )}
                                  </td>
                                  {viewOption(
                                    "customerSettings",
                                    "isReferenceId",
                                  ) && (
                                    <td className="text-center">
                                      {item?.referenceid || "-"}
                                    </td>
                                  )}
                                  <td className="text-center">{item?.name}</td>
                                  <td className="text-center text-heading fw-medium">
                                    +91 {item?.phone}
                                  </td>
                                  <td className="text-center">
                                    {item?.email || "-"}
                                  </td>
                                  {/* <td className="text-center">
                                    {item?.state || "-"}
                                  </td>
                                  <td className="text-center">
                                    {item?.pincode || "-"}
                                  </td> */}
                                  <td className="text-center">
                                    {item?.gstnumber || "-"}
                                  </td>
                                  {/* <td className="text-center">
                                {item.status && (
                                  <span
                                    className={`badge rounded-pill ${getColor(
                                      item.status
                                    )}`}
                                  >
                                    {item.status.charAt(0).toUpperCase() +
                                      item.status.slice(1).toLowerCase()}
                                  </span>
                                )}
                              </td> */}

                                  {/* <td className="text-center">
                                    <div className="order-actions opacity-0 opacity-md-100">
                                      <button
                                        type="button"
                                        className="btn-label-info shadow-none me-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleView(item)}
                                        }
                                      >
                                        <i className="bx bx-show"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-label-primary shadow-none me-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditModal(item)}
                                        }
                                      >
                                        <i className="ng ng-ng-edit-icon"></i>
                                      </button>

                                      <button
                                        type="button"
                                        className="btn-label-danger shadow-none me-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openDeleteModal(item)
                                        }}
                                      >
                                        <i className="ng ng-ng-delete-icon"></i>
                                      </button>
                                    </div>
                                  </td> */}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="9"
                                  className="text-center p-2 text-muted"
                                >
                                  <span className="text-muted">
                                    No customers found in the store. add some
                                    customers to get started.
                                  </span>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {totalPages > 0 && (
                        <div className="d-flex justify-content-center mt-3">
                          <ReactPaginate
                            previousLabel={"← Previous"}
                            nextLabel={"Next →"}
                            breakLabel={"..."}
                            onPageChange={({ selected }) => {
                              setIsLoading(true);
                              setCurrentPage(selected);
                              handleListCustomer(
                                selected,
                                isSearchTerm,
                                selectedFilter.field,
                                selectedFilter.order,
                              );
                            }}
                            pageRangeDisplayed={5}
                            pageCount={totalPages}
                            containerClassName="pagination"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            activeClassName="active"
                            forcePage={currentPage}
                          />
                        </div>
                      )}
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <AddCustomer
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onAdd={handleAdd}
          isBtnLoading={isBtnLoading}
          groupId={selectedGroupId}
          selectedGroupDetails={selectedGroupDetails}
          onEditGroupData={handleCustomerGroupEdit}
          groupName={groupName}
          onChangeGroup={handleGroupChange}
          onNewGroupsIn={handleCustomerGroupAdd}
          newGroup={newGroupName}
        /> */}
        <AddCustomer
          isActive={isActive}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onData={isCustomerData}
          onAdd={handleAdd}
          isBtnLoading={isBtnLoading}
          groupName={groupName}
          onNewGroupsIn={handleCustomerGroupAdd}
          newGroup={newGroupName}
          resetAll={true}
          error={error}
          setErrors={setErrors}
        />
        {isViewModalOpen && (
          <CustomerView
            isOpen={isViewModalOpen}
            onClose={() => setViewModalOpen(false)}
            customerId={selectedCustomerId}
            onUpdate={hanleUpdateCustomer}
            isActive={isActive}
            OnDelete={handleDeleteCustomer}
          />
        )}

        {isEditModalOpen && (
          <EditCutomer
            isActive={isActive}
            editData={isEditData}
            onClose={closeEditModal}
            onEdit={handleUpdte}
            isBtnLoading={isBtnLoading}
            groupId={selectedGroupId}
            selectedGroupDetails={selectedGroupDetails}
            onEditGroupData={handleCustomerGroupEdit}
            groupName={groupName}
            onChangeGroup={handleGroupChange}
            onNewGroupsIn={handleCustomerGroupAdd}
            newGroup={newGroupName}
            error={error}
            setErrors={setErrors}
          />
        )}

        {isDeleteModalOpen && (
          <DeleteModal
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={handleDelete}
            isLable={" Customer " + isDeleteData?.name}
            isBtnLoading={isBtnLoading}
          />
        )}
        {addNewGroupOpen && (
          <AddGroup
            onClose={handleNewGroupClose}
            onNewGroupIn={handleCustomerGroupAdd}
            isBtnLoading={addGroupload}
          />
        )}
        {editGroupOpen && (
          <EditGroup
            onClose={handleEditGroupClose}
            onNewGroupIn={handleCustomerGroupEdit}
            isBtnLoading={addGroupload}
            onData={selectedGroupDetails}
          />
        )}
      </div>
    </div>
  );
};

export default AllCustomer;
